import * as Sentry from '@sentry/react';

/**
 * Falha na listagem de tickets (fila do Meu Workspace, dock, notificações). Até o
 * CORE-4167 o erro era engolido (`onRequestError: () => {}`): a tela só sabia que
 * falhou, sem status HTTP, e nada chegava ao Sentry — o diagnóstico do CORE-4093
 * dependeu de abrir o DevTools da agente para ler o `developerMessage`.
 *
 * Só campos não sensíveis saem daqui: status, mensagem técnica do mt-ticket e os
 * parâmetros de busca por id. Nunca documento, token nem o corpo inteiro da resposta.
 */
export type TicketListError = {
  status: number | null;
  developerMessage: string | null;
};

export const TICKET_LIST_ERROR_FEATURE = 'attendance-queue';

export function parseTicketListError(err: any): { error: TicketListError } {
  return {
    error: {
      status: err?.response?.status ?? null,
      developerMessage: err?.response?.data?.developerMessage ?? null,
    },
  };
}

const SAFE_PARAMS = ['responsibleId', 'statusIds', 'page', 'size', 'sort', 'start', 'end'] as const;

// Exceção própria no lugar do AxiosError cru: título legível no Sentry e garantia de que só o
// status HTTP vai na mensagem (nada da resposta, da config nem dos headers da request).
export class TicketListRequestError extends Error {
  constructor(status: number | null) {
    super(`Falha ao listar tickets: HTTP ${status ?? 'network'}`);
    this.name = 'TicketListRequestError';
  }
}

export function reportTicketListError(err: any, params?: Record<string, unknown>, meta?: { actionId?: string }) {
  const { error } = parseTicketListError(err);
  const extra: Record<string, unknown> = { ...error };
  SAFE_PARAMS.forEach((key) => {
    if (params?.[key] !== undefined) extra[key] = params[key];
  });

  const context = {
    tags: {
      feature: TICKET_LIST_ERROR_FEATURE,
      step: meta?.actionId || 'ticket-list',
      status: String(error.status ?? 'network'),
    },
    extra,
  };

  // 4xx é resultado esperado (403 sem permissão, 400 filtro inválido): fica registrado com status e
  // contexto, mas como aviso, sem virar issue de erro. 5xx e rede são falha de verdade.
  if (error.status != null && error.status < 500) {
    Sentry.captureMessage(`Fila de tickets recusada: HTTP ${error.status}`, { level: 'warning', ...context });
    return;
  }
  Sentry.captureException(new TicketListRequestError(error.status), context);
}
